import React, { useState, useEffect, Suspense } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Settings, User, Landmark, Calendar, Lightbulb, ArrowLeft, Brain, Briefcase, BookOpen, GraduationCap, Search, MessageSquare, ShoppingCart, SquareCheckBig } from 'lucide-react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { authService } from '../../lib/auth-service';
import AvatarOrInitial from '../Family/AvatarOrInitial';

// Lazy load the Malcolm content component
const MalcolmMobileContent = React.lazy(() => import('./MalcolmMobileContent'));

export default function MobileHamburgerMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const { currentUser, setCurrentUser, family } = useFamilyContext();
  const navigate = useNavigate();
  const location = useLocation();

  const isParent = currentUser?.isParent || currentUser?.originalParent;
  const isOnMalcolmPage = location.pathname === '/ai-intelligence';

  // Close menu when route changes
  useEffect(() => {
    const handleRouteChange = () => setIsOpen(false);
    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const menuItems = [
    // Activities not in main nav
    {
      key: 'income-hub',
      label: 'Income Hub',
      path: '/income-hub',
      icon: <Landmark className="w-5 h-5" />,
      color: 'teal'
    },
    {
      key: 'recs-ideas',
      label: 'Recs & Ideas',
      path: '/recs-ideas',
      icon: <Lightbulb className="w-5 h-5" />,
      color: 'indigo'
    },
    {
      key: 'packing',
      label: 'Packing',
      path: '/packing',
      icon: <Briefcase className="w-5 h-5" />,
      color: 'indigo'
    },
    {
      key: 'summer-explorer',
      label: 'College Prep',
      path: '/summer-explorer',
      icon: <Search className="w-5 h-5" />,
      color: 'coral',
      isBeta: true
    },
    {
      key: 'college-journey',
      label: 'College Journey',
      path: '/college',
      icon: <GraduationCap className="w-5 h-5" />,
      color: 'slate',
      isBeta: true
    },
    {
      key: 'reading',
      label: 'Reading Challenge',
      path: '/reading',
      icon: <BookOpen className="w-5 h-5" />,
      color: 'magenta'
    },
    {
      key: 'ai-intelligence',
      label: 'AI Intelligence Feed',
      path: '/ai-intelligence',
      icon: <Brain className="w-5 h-5" />,
      color: 'indigo',
      isBeta: true
    },
    {
      key: 'intelligence-dashboard',
      label: 'Intelligence Dashboard',
      path: '/intelligence-dashboard',
      icon: <Brain className="w-5 h-5" />,
      color: 'indigo',
      isBeta: true
    }
  ];

  // Apply dynamic paths and filter menu items based on user role
  const processedMenuItems = menuItems.map(item => {
    // Apply dynamic path logic like SideNav
    if (item.key === 'reading') {
      return {
        ...item,
        path: isParent ? '/reading/admin' : '/reading'
      };
    }
    return item;
  });

  const visibleMenuItems = processedMenuItems.filter(item => {
    if (item.roles && !item.roles.includes(isParent ? 'parent' : 'kid')) {
      return false;
    }
    return true;
  });



  const returnToParentView = async () => {
    const user = await authService.getUser();
    if (!user) return;

    localStorage.removeItem(`family-data-${user.id}`);
    localStorage.removeItem(`family-data-timestamp-${user.id}`);

    const loggedInParent = family?.parents?.find(p => p.authUserId === user?.id);
    if (loggedInParent) {
      setCurrentUser({
        ...loggedInParent,
        isParent: true,
        originalParent: false
      });
      navigate('/parent-dashboard');
      setIsOpen(false);
    }
  };

  const getUserInitials = () => {
    if (!currentUser?.name) return 'U';
    return currentUser.name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const handleNavClick = () => {
    // Close menu after navigation
    setTimeout(() => setIsOpen(false), 150);
  };

  return (
    <>
      {/* Hamburger Menu Button - Only visible on mobile */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed top-4 right-4 z-50 p-2 bg-white rounded-lg shadow-md border border-app-subtle hover:bg-stone-50 transition-colors md:hidden"
        aria-label="Open menu"
      >
        <Menu className="w-5 h-5 text-app-primary" />
      </button>

      {/* Menu Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Menu Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 transition-transform duration-300 ease-in-out md:hidden ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="p-5 border-b border-app-subtle">
          <div className="flex items-center justify-between">
            <h2 className="font-light text-xl text-app-primary">Menu</h2>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              aria-label="Close menu"
            >
              <X className="w-5 h-5 text-app-tertiary" />
            </button>
          </div>
        </div>

        {/* User Profile Section */}
        <div className="p-5 border-b border-app-subtle">
          <div 
            onClick={() => {
              navigate('/account-settings');
              handleNavClick();
            }}
            className="flex items-center gap-3 cursor-pointer hover:bg-stone-50 rounded-xl p-2 -m-2 transition-colors"
          >
            <div className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center text-white font-semibold">
              {getUserInitials()}
            </div>
            <div className="flex-1">
              <div className="font-semibold text-app-primary">
                {currentUser?.name || 'User'}
              </div>
              <div className="text-sm text-app-subtle">
                {isParent ? 'Parent' : 'Kid'} • Account Settings
              </div>
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <nav className="p-4">
          <ul className="space-y-2">
            {visibleMenuItems.map(item => (
              <li key={item.key}>
                <NavLink
                  to={item.path}
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 ${
                      isActive
                        ? 'bg-stone-950 text-white shadow-sm'
                        : 'text-stone-800 hover:bg-stone-50 hover:text-app-primary'
                    }`
                  }
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  <span className="flex-1">{item.label}</span>
                  {item.isBeta && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full">
                      Beta
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Malcolm Sidebar Content - Show when on AI Intelligence Feed page */}
        {isOnMalcolmPage && (
          <Suspense fallback={
            <div className="border-t border-app-subtle p-4">
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="text-stone-600 text-sm mt-2">Loading AI Tools...</p>
              </div>
            </div>
          }>
            <MalcolmMobileContent />
          </Suspense>
        )}

        {/* Return to Parent View */}
        {currentUser?.originalParent && (
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-app-subtle bg-white">
            <button
              onClick={returnToParentView}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Return to Parent View</span>
            </button>
          </div>
        )}
      </div>
    </>
  );
}